'use client';

import {useParams} from 'next/navigation';
import {useTranslation} from 'react-i18next';
import {AnalyticsEntryPoint, detailAnalytics, trackUnitTypeFloorPlanClick} from '../analytics';
import {HeroGallery} from '../components/gallery';
import {
  DetailAmenities,
  DetailMasterplanSection,
  DetailNearby,
  DetailNewsCarousel,
  DetailPageModals,
  DetailSummary,
  PropertyEyebrow,
  toMediaAssets,
  UnitAboutSection,
  UnitFloorPlanSection,
  useDetailModals,
} from '../components/properties';
import {PageRegisterInterestContext} from '../components/register-interest/PageRegisterInterestContext';
import type {NewsCardView, UnitTypeDetailView} from '../model';
import {projectHref} from '../routing';

export interface UnitDetailPageProps {
  unit: UnitTypeDetailView;
  latestNews: NewsCardView[];
}

const ENTRY_POINT = AnalyticsEntryPoint.UnitTypeDetailsPage;

export function UnitDetailPage({unit, latestNews}: UnitDetailPageProps) {
  const {t} = useTranslation();
  const {locale} = useParams<{locale: string}>();
  const analytics = detailAnalytics(ENTRY_POINT);
  const tracked = {id: unit.uuid, name: unit.name};
  const modals = useDetailModals(unit);

  return (
    <>
      <PageRegisterInterestContext projectId={unit.projectUuid} designTypeId={unit.uuid} />
      <HeroGallery
        assets={toMediaAssets(unit.gallery)}
        title={unit.name}
        onOpen={() => {
          analytics.gallery(tracked);
        }}
      />
      <DetailSummary
        item={unit}
        analytics={analytics}
        registerInterestProjectId={unit.projectUuid}
        registerInterestDesignTypeId={unit.uuid}
        eyebrow={
          <PropertyEyebrow
            primaryLabel={unit.contextLabel}
            primaryHref={projectHref(locale, unit.projectUuid)}
            onPrimaryClick={() => {
              analytics.labelClick({id: unit.projectUuid, name: unit.contextLabel});
            }}
            secondaries={unit.soldOut ? [{label: t('sold_out')}] : undefined}
          />
        }
      />
      <UnitAboutSection
        description={unit.description}
        specs={unit.specs}
        brochureUrl={unit.brochureUrl}
        name={unit.name}
        onDownloadBrochure={() => {
          analytics.downloadBrochure(tracked);
        }}
      />
      <UnitFloorPlanSection
        floorPlans={unit.floorPlans}
        onOpen={() => {
          trackUnitTypeFloorPlanClick(tracked);
        }}
      />
      <DetailAmenities item={unit} analytics={analytics} modals={modals} />
      <DetailMasterplanSection
        media={unit.masterplan}
        onView={() => {
          analytics.masterplan(tracked);
        }}
      />
      <DetailNearby item={unit} analytics={analytics} modals={modals} />
      <DetailNewsCarousel articles={latestNews} analytics={analytics} parentTracked={tracked} viewAllProjectId={unit.projectUuid} />
      <DetailPageModals amenities={unit.amenities} modals={modals} />
    </>
  );
}

UnitDetailPage.displayName = 'UnitDetailPage';
